import {
  assertSupportedCompositionVersion,
  COMPOSITION_VERSION,
  type CompositionVersion,
} from "@hightouch/lib/ad-studio/html-editor/animation-blocks";
import type { AnimationDescriptor } from "@hightouch/lib/ad-studio/html-editor/model";

/**
 * Prefix used by agent-generated custom Remotion components, e.g.
 * `./assets/light-sweep.tsx`. Layers whose `component` starts with this are
 * resolved against a runtime-loaded `customRegistry` rather than the built-in
 * one. Defined here (a DOM-free module) so both the loader and the
 * schema-defaults pass can reference it without pulling in the DOM-dependent
 * renderer chain.
 */
export const CUSTOM_COMPONENT_PREFIX = "./assets/";

export function isCustomComponent(component: string): boolean {
  return component.startsWith(CUSTOM_COMPONENT_PREFIX);
}

export function isCustomComponentLayer(layer: CompositionLayer): boolean {
  return isCustomComponent(layer.component);
}

/**
 * Built-in layer component names. Canonical here (a DOM-free module) so the
 * track derivation and the renderer can both reference them without pulling in
 * the DOM-dependent component chain.
 */
export const LAYER_COMPONENT_VIDEO = "@ad-studio/video";
/**
 * Legacy layer type. Nothing renders it any more; the v1 to v2 backfill
 * converts every stored one to an html-overlay layer.
 */
export const LAYER_COMPONENT_ANIMATED_SVG = "@ad-studio/animated-svg";
export const LAYER_COMPONENT_HTML_OVERLAY = "@ad-studio/html-overlay";
export const LAYER_COMPONENT_CAPTIONS = "@ad-studio/captions";
export const LAYER_COMPONENT_AUDIO = "@ad-studio/audio";

export { assertSupportedCompositionVersion, COMPOSITION_VERSION };
export type { CompositionVersion };

export interface VideoComposition {
  /**
   * Animation-system version. `2` = the HTML overlay's `animations` sidecar
   * holds `AnimationBlock`s. Absent or `1` is the legacy descriptor format
   * (`AnimationDescriptor` + Feel presets), which this build does not render;
   * the v1 to v2 backfill rewrites stored compositions to `2`. Untrusted
   * composition JSON can carry anything here regardless of this type, so
   * render paths must call `assertSupportedCompositionVersion` — an
   * unknown version fails loudly instead of silently dropping motion.
   */
  version?: CompositionVersion;
  fps: number;
  width: number;
  height: number;
  layers: CompositionLayer[];
  /**
   * Timeline tracks, ordered bottom-first to match `layers` — later entries
   * paint on top. Each layer belongs to exactly one track via `trackId`, and
   * layers sharing a track are expected to be disjoint in time.
   *
   * Absent means "derive from `layers`", which is the case for every
   * composition written before tracks existed; `resolveTracks` packs those
   * into tracks on read without changing what renders. Present means
   * authoritative — the user has committed to a layout, so it is never
   * re-derived even if the packing heuristic would now choose differently.
   */
  tracks?: Track[];
}

/**
 * A timeline track: a horizontal lane holding clips laid end to end. Track
 * order in `VideoComposition.tracks` is the z-order (bottom-first); within a
 * track, clips are disjoint in time, so their relative z is unobservable.
 */
export interface Track {
  id: string;
  /** Shown in the track header. Falls back to a label derived from `kind`. */
  label?: string;
  /** Required: a track's kind determines what may be placed on it, keeps
   *  unlike layers off the same lane, and decides whether its clips take part
   *  in visual transitions (audio needs volume ramps instead). */
  kind: TrackKind;
  /**
   * Editor-only: blocks mutation of the track and its clips. Ignored at render
   * time, mirroring the per-layer `locked` prop.
   */
  locked?: boolean;
  /**
   * Skips every clip on the track at render time, so — like the per-layer
   * `hidden` prop — the hidden state carries into the exported creative.
   */
  hidden?: boolean;
}

/**
 * What a track holds. Drives default labels and keeps unlike layers off the
 * same track when tracks are derived (a video clip and an SVG overlay may be
 * disjoint in time, but sharing a lane reads as a mistake).
 */
export type TrackKind = "video" | "overlay" | "captions" | "audio";

export interface CompositionLayer {
  id: string;
  component: string;
  from: number;
  duration: number;
  /**
   * The track this clip belongs to. Absent means unassigned — `resolveTracks`
   * places it by the same packing rule used for derivation, which is the state
   * of any layer added by the ad agent, asset import, or the disclaimer
   * overlay before those learn about tracks.
   */
  trackId?: string;
  /**
   * Editor-owned. Offset into the layer's content at which playback starts, so
   * a clip trimmed at the head keeps its place on the timeline while showing
   * later source. `CompositionRoot` applies it once for every component; the
   * agent carries an existing value through a rewrite and never authors one.
   * Read via `getLayerTrim`, which also covers the legacy `props.trim` spot.
   */
  trim?: LayerTrim;
  /**
   * Component-specific props (see `HtmlOverlayProps`, `VideoLayerProps`, …).
   * Two keys are editor-managed and honored regardless of component:
   * `hidden` (boolean) — `CompositionRoot` skips the layer entirely, so the
   * hidden state persists into the exported creative; `locked` (boolean) —
   * editor-only, blocks mutation of the layer and is ignored at render time.
   */
  props: Record<string, unknown>;
}

/**
 * `startFrame` is the in-point: the first content frame that plays. `endFrame`
 * is derived (`startFrame + duration * playbackRate`) and only the editor's
 * speed and edge-drag math reads it; the layer's `durationInFrames` already
 * clips the tail at render time.
 */
export interface LayerTrim {
  startFrame: number;
  endFrame?: number;
}

/**
 * Optional spatial placement for a composition layer. When omitted, the
 * layer renders at full composition size (the historical default). When set,
 * `CompositionRoot` wraps the layer in an absolutely-positioned container
 * sized to these dimensions, so the layer's internal `<AbsoluteFill>` fills
 * the wrapper instead of the whole canvas.
 *
 * Coordinates are in the composition's pixel space (the same units as
 * `VideoComposition.width` / `.height`). Origin is the top-left of the
 * composition.
 */
export interface LayerTransform {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Fade in/out for a layer that carries audio, in wall-clock seconds rather
 * than frames. `media-volume.ts` resolves them against the clip's length
 * and the composition fps.
 */
export type MediaFadeProps = {
  fadeInSeconds?: number;
  fadeOutSeconds?: number;
};

export interface VideoLayerProps extends MediaFadeProps {
  src: string;
  volume?: number;
  muted?: boolean;
  /**
   * Playback speed multiplier; 1 (or omitted) is normal speed. Changing it
   * changes the clip's on-screen duration — the trimmed source range stays
   * fixed, so at 2x the clip occupies half its timeline slot and at 0.5x it
   * occupies double (on-screen frames = source span / playbackRate).
   * Speed preserves pitch unless `preservePitch` is `false`.
   */
  playbackRate?: number;
  /**
   * When speed is not 1, keep the original pitch (time-stretch). Omitted or
   * true is on; only `false` opts out so pitch rises and falls with rate.
   */
  preservePitch?: boolean;
  transform?: LayerTransform;
  /**
   * Optional crop: where the full video frame sits behind its window, in
   * composition pixels measured from the window's top-left. The window clips
   * it (overflow:hidden), so panning moves `content` and zooming scales it,
   * and by construction it always fully covers the window. When omitted, the
   * video fills the window via objectFit:cover (the historical, uncropped
   * behavior).
   *
   * The window is `transform` when the layer sets one, otherwise the canvas.
   * Cropping and placement are independent — a full-bleed crop needs no
   * `transform`, and moving a placed layer leaves its framing alone. See
   * `resolveCropWindow`.
   */
  content?: LayerTransform;
  /**
   * Historical `ad:` ref (or resolved URL) to a static first-frame PNG.
   * New saves persist `<video-filename>.thumbnail.png` in component assets and
   * preview callers resolve it without storing this indirection.
   *
   * @deprecated Retained for compositions saved before canonical thumbnail
   * filenames were introduced.
   */
  thumbnailUrl?: string;
}

/**
 * The legacy animation entry, authored by `animated-svg` layers and read by
 * the v1 to v2 backfill. Lives in `@hightouch/lib` next to the Doc model
 * because lib cannot depend on this package; re-exported here.
 */
export type { AnimationDescriptor };

/**
 * `@ad-studio/audio` — a layered audio track (music bed or voiceover) mixed
 * into the composition. Placed on the timeline via `CompositionLayer.from` /
 * `duration` (frames), like any other layer.
 *
 * `volume` is linear 0-1 (default 1), the same unit as the video layer's
 * `volume` — the peak level the envelope holds between fades. Fades are
 * wall-clock seconds (0 / omitted = no fade), converted to frames at the
 * composition fps. `playbackRate` is the same unit as video. Speed preserves
 * pitch unless `preservePitch` is `false` (tape-style, pitch follows rate).
 * Omitted `preservePitch` is on.
 */
export interface AudioLayerProps extends MediaFadeProps {
  src: string;
  volume?: number;
  playbackRate?: number;
  /**
   * When speed is not 1, keep the original pitch (time-stretch). Omitted or
   * true is on; only `false` opts out so pitch rises and falls with rate.
   */
  preservePitch?: boolean;
}
